import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import { notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { money } from "./ffUi";

// FF-12: client-side net balance (debit - credit) rendered as "x Dr" / "x Cr"
const netBalance = (debit, credit) => {
  const net = (Number(debit) || 0) - (Number(credit) || 0);
  if (net > 0) return `${money(net)} Dr`;
  if (net < 0) return `${money(-net)} Cr`;
  return "0.00";
};

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const TrialBalance = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [isBalanced, setIsBalanced] = useState(null);
  const [totals, setTotals] = useState({ debit: 0, credit: 0 });

  useEffect(() => {
    getTrialBalance();
  }, []);

  // Per-row debit/credit may come under different keys depending on backend
  const getDebit = (r) =>
    Number(
      r.debit ?? r.debit_amount ?? r.total_debit ?? r.debit_balance ?? 0
    ) || 0;
  const getCredit = (r) =>
    Number(
      r.credit ?? r.credit_amount ?? r.total_credit ?? r.credit_balance ?? 0
    ) || 0;

  const getTrialBalance = () => {
    setLoading(true);
    simplePostCall(ApiConfig.FLEET_FINANCE_TRIAL_BALANCE, JSON.stringify({}))
      .then((data) => {
        if (data?.success || data?.result) {
          const d = data?.data;
          const list = Array.isArray(d) ? d : d?.accounts || d?.rows || [];
          setRows(list);
          // is_balanced lives at the top level (sibling of data)
          setIsBalanced(
            data?.is_balanced ?? d?.is_balanced ?? null
          );
          // totals live in a top-level `totals` object: { total_debit, total_credit }
          const totalsObj = data?.totals || d?.totals || {};
          setTotals({
            debit:
              totalsObj.total_debit ??
              data?.total_debit ??
              list.reduce((s, r) => s + getDebit(r), 0),
            credit:
              totalsObj.total_credit ??
              data?.total_credit ??
              list.reduce((s, r) => s + getCredit(r), 0),
          });
        } else {
          notifyError(data?.message || "Unable to load trial balance");
          setRows([]);
        }
      })
      .catch((err) => {
        console.log("trial balance err", err);
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_TrialBalance">
        <div className="main-master-wrapper">
          <div
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{
              borderBottom: "0.5px solid #f6efe9",
              paddingBottom: 12,
              marginBottom: 20,
              gap: 12,
            }}
          >
            <div className="vehicleHead">
              <p style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
                {t("Trial Balance")}
              </p>
            </div>
            {isBalanced !== null && (
              <span
                style={{
                  color: isBalanced ? "green" : "red",
                  fontWeight: 600,
                }}
              >
                {isBalanced ? t("Balanced") : t("Not Balanced")}
              </span>
            )}
          </div>

          {loading ? (
            <Loader />
          ) : (
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Code")}</th>
                      <th>{t("Account Name")}</th>
                      <th>{t("Type")}</th>
                      <th>{t("Debit")}</th>
                      <th>{t("Credit")}</th>
                      <th>{t("Net Balance")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {rows && rows.length > 0 ? (
                      <>
                        {rows.map((r, i) => (
                          <tr key={r.gl_account_id || i}>
                            <td>{r.account_code}</td>
                            <td>{r.account_name}</td>
                            <td>{r.account_type}</td>
                            <td>{getDebit(r).toLocaleString()}</td>
                            <td>{getCredit(r).toLocaleString()}</td>
                            {/* FF-12 */}
                            <td>{netBalance(getDebit(r), getCredit(r))}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 700 }}>
                          <td colSpan={3}>{t("Total")}</td>
                          <td>{Number(totals.debit).toLocaleString()}</td>
                          <td>{Number(totals.credit).toLocaleString()}</td>
                          <td>{netBalance(totals.debit, totals.credit)}</td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default TrialBalance;
